// cspell:ignore IHDR
/**
 * Watermark image processing (VE-24693).
 *
 * An upload is rasterized once, on the main thread, with a `<canvas>` — SVGs
 * become PNG because `mupdf.Image` can't decode SVG, and everything is bounded
 * to RASTER_MAX_EDGE. That rasterized PNG is the **base**, and it keeps the
 * image's ORIGINAL COLORS.
 *
 * That last part is load-bearing. The ticket's 2026-08-17 amendment supersedes
 * the original "images display in grayscale" criterion with: both image and text
 * watermarks support color, and the user may render EITHER in grayscale.
 * So grayscale is a reversible per-watermark choice applied over a color base, not a
 * property baked into the upload — an earlier revision converted every upload
 * to grayscale here, which destroyed the image's colors at source and made the
 * amended criterion unreachable.
 *
 * The drawn form is derived from the base on demand:
 *   base -> `toGrayscaleImage` when the watermark is set to grayscale
 *   base -> `recolorWatermarkImage` when a tint color is chosen
 * Derivation stays here on the main thread, so no color work happens at export
 * time. The preview draws the resulting PNG as-is; the burn-in worker may
 * downscale it to its drawn size (`fitWatermarkImage`).
 *
 * Pixel math (`hexToRgb`, `luminance`, `toGrayscale`, `tintGrayscale`,
 * `grayscaleHex`) is factored into pure, DOM-free functions so it can be
 * unit-tested; the `<canvas>`/`Image` glue is browser-only.
 */
import { MAX_STORED_IMAGE_EDGE, RASTER_MAX_EDGE } from './watermarkImageLimits';
import { WATERMARK_WHITE_REMOVAL } from './watermarkWhiteRemoval';

/** Accepted upload types (AC#2): .svg, .png, .jpg/.jpeg. */
export const WATERMARK_IMAGE_ACCEPT =
  '.svg,.png,.jpg,.jpeg,image/svg+xml,image/png,image/jpeg';

/**
 * Maximum accepted upload size. The ticket left this "(TBD)"; the approved
 * design said 2 MB, raised to 5 MB once artwork stopped being stored inline on
 * the SDO row (§K). SEC-R1 requires an explicit maximum on every new input
 * surface — it does not require a small one, and the number now bounds what we
 * are willing to DECODE rather than what we are willing to store. What is
 * stored is our own re-encode, bounded separately by RASTER_MAX_EDGE and by
 * {@link MAX_STORED_IMAGE_DATA_URL_CHARS}.
 *
 * Keep {@link MAX_WATERMARK_IMAGE_LABEL} in step — the limit has to be visible
 * before the user trips it, not only after.
 */
export const MAX_WATERMARK_IMAGE_BYTES = 5 * 1024 * 1024;

/** Human-readable form of {@link MAX_WATERMARK_IMAGE_BYTES} for helper text. */
export const MAX_WATERMARK_IMAGE_LABEL = '5 MB';

/** Parse a hex color ("#RGB" or "#RRGGBB") to a 0..255 RGB triple, or null. */
export function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) {
    return null;
  }
  let h = m[1] as string;
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

/** Rec. 601 luma of an 8-bit RGB pixel. */
export function luminance(r: number, g: number, b: number): number {
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

/**
 * Convert RGBA pixel data to grayscale in place (alpha preserved). Mutates and
 * returns the same array so callers can `putImageData` it back. Pure w.r.t. the
 * DOM — takes/returns a plain typed array so it's unit-testable.
 */
export function toGrayscale(data: Uint8ClampedArray): Uint8ClampedArray {
  for (let i = 0; i < data.length; i += 4) {
    const y = luminance(data[i] ?? 0, data[i + 1] ?? 0, data[i + 2] ?? 0);
    data[i] = y;
    data[i + 1] = y;
    data[i + 2] = y;
  }
  return data;
}

/**
 * How light a pixel must be to count as PAPER rather than artwork.
 *
 * Read on the smallest channel, not on luminance: a saturated yellow has a high
 * luminance and a low blue channel, and keying it out because it looked bright
 * would eat the artwork. Requiring every channel to be near 255 means "this is
 * white", not "this is light".
 */
const PAPER_MIN_CHANNEL = 240;

/**
 * The bottom of the feather band, below which a pixel is left fully opaque.
 *
 * Anti-aliased artwork edges are a blend of the logo and the paper behind it.
 * Keyed with a hard threshold they survive as a pale outline — the halo that
 * makes a knocked-out logo look cut out with scissors. Pixels in this band that
 * touch the removed background get an alpha proportional to how far from white
 * they are, so the edge fades the way the original did.
 */
const FRINGE_MIN_CHANNEL = 170;

/**
 * Whether white ENCLOSED by artwork counts as part of the logo.
 *
 * ON, which is what the key does today: it reaches in from the border only, so
 * white the fill cannot walk to is surrounded by artwork and is kept. A white
 * wordmark inside a dark roundel survives, and so does the counter of an O.
 *
 * Off keys every near-white pixel wherever it sits. That is the right answer
 * for a logo whose interior white is not drawn on purpose but is just the page
 * showing through a gap — kept, that white paints an opaque patch onto the
 * document over whatever was underneath, which is the defect this whole pass
 * exists to remove, reappearing in the middle of the mark instead of around it.
 *
 * Nothing in a raster tells the two apart; only whoever drew the logo knows. So
 * this is a switch to flip against a real library rather than a guess, and the
 * parameter below exists so the tests can pin BOTH behaviours regardless of
 * which one is currently shipping.
 */
export const PRESERVE_ENCLOSED_WHITE = true;

/**
 * Whether a key should be abandoned and the mark drawn unkeyed instead.
 *
 * SOME ARTWORK IS PAPER BY THIS TEST. A logo delivered white-on-transparent —
 * the usual asset for dark backgrounds — is `alpha 0` or `>= 240` on every
 * channel, and transparent pixels are walked through deliberately so the fill
 * can get past a PNG's own alpha border, so the fill reaches all of it. An
 * opaque near-neutral logo paler than about #F1F1F1 goes the same way. Both
 * come back completely erased, and erased SILENTLY: nothing throws, the
 * re-encoded PNG is valid, the panel sees a mark that has a raster, and the
 * operator gets an invisible watermark and an unmarked export with nothing said
 * anywhere.
 *
 * The test is ARTWORK, not coverage. An earlier version compared opaque pixels
 * before and against after, which measured the wrong thing entirely: `before`
 * counts the paper too, so the ratio said what fraction of the canvas is ink
 * rather than how much ink survived. A perfectly ordinary logo — a wordmark on
 * a white square, ink over maybe 2% of the raster — tripped it, the key was
 * abandoned, and the white box burned in. The guard broke the feature it exists
 * to protect, on the common case.
 *
 * Asking about artwork cannot make that mistake, because the fill only ever
 * clears pixels that are already paper: anything that is not paper survives by
 * construction. So a mark is erased exactly when it had no non-paper pixels to
 * keep. `opaqueAfter` is belt and braces for a shape nobody has thought of.
 */
export function keyWouldEraseMark(
  artworkBefore: number,
  opaqueAfter: number
): boolean {
  return artworkBefore === 0 || opaqueAfter === 0;
}

/**
 * Make a logo's PAPER transparent, so only its artwork marks the page.
 *
 * A logo is a rectangle and the artwork inside it is not, so its corners are
 * white — and that white, composited at any real opacity, washes out whatever
 * it covers. This removes it, which lets the mark be drawn with ordinary
 * compositing: the artwork still darkens a black redaction box the way it
 * always did, and a pale logo stays pale instead of being multiplied away.
 *
 * FLOOD-FILLED FROM THE BORDER while {@link PRESERVE_ENCLOSED_WHITE} is on,
 * rather than keyed per pixel. White that is enclosed BY the artwork — a white
 * wordmark inside a dark roundel, the counters of an O — may be part of the
 * logo, and a per-pixel key cannot tell that from the corners. Reaching in from
 * the edges can: anything the fill cannot walk to is inside the logo and is
 * kept. With the flag off, every near-white pixel goes wherever it sits; the
 * flag's own note says why neither answer is right for all artwork.
 *
 * A pixel that is already transparent counts as paper too, so the fill walks
 * through the border of a PNG that arrived with its own alpha instead of
 * stopping dead at the first transparent pixel.
 *
 * Mutates and returns `data`, like the two above.
 */
export function knockOutBackground(
  data: Uint8ClampedArray,
  width: number,
  height: number,
  preserveEnclosed: boolean = PRESERVE_ENCLOSED_WHITE
): Uint8ClampedArray {
  const count = width * height;
  if (count <= 0 || data.length < count * 4) {
    return data;
  }
  const minChannel = (i: number): number =>
    Math.min(data[i * 4] ?? 0, data[i * 4 + 1] ?? 0, data[i * 4 + 2] ?? 0);
  const isPaper = (i: number): boolean =>
    (data[i * 4 + 3] ?? 0) < 8 || minChannel(i) >= PAPER_MIN_CHANNEL;

  const background = new Uint8Array(count);
  if (preserveEnclosed) {
    // Reach in from the BORDER only. Whatever the fill cannot walk to is
    // surrounded by artwork and is kept — see PRESERVE_ENCLOSED_WHITE.
    const stack: number[] = [];
    const visit = (i: number): void => {
      if (!background[i] && isPaper(i)) {
        background[i] = 1;
        stack.push(i);
      }
    };
    for (let x = 0; x < width; x++) {
      visit(x);
      visit((height - 1) * width + x);
    }
    for (let y = 0; y < height; y++) {
      visit(y * width);
      visit(y * width + width - 1);
    }
    while (stack.length > 0) {
      const i = stack.pop() as number;
      const x = i % width;
      const y = (i - x) / width;
      if (x > 0) {
        visit(i - 1);
      }
      if (x < width - 1) {
        visit(i + 1);
      }
      if (y > 0) {
        visit(i - width);
      }
      if (y < height - 1) {
        visit(i + width);
      }
    }
  } else {
    // Every paper pixel goes, wherever it sits.
    for (let i = 0; i < count; i++) {
      if (isPaper(i)) {
        background[i] = 1;
      }
    }
  }

  // The feather is computed against the ORIGINAL background map rather than as
  // the alphas are written, so it stays one pixel deep. Cascading it would eat
  // inward through a soft-edged logo until nothing opaque was left.
  for (let i = 0; i < count; i++) {
    if (background[i]) {
      continue;
    }
    const x = i % width;
    const y = (i - x) / width;
    let touchesBackground = false;
    for (let dy = -1; dy <= 1 && !touchesBackground; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) {
          continue;
        }
        const nx = x + dx;
        const ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= width || ny >= height) {
          continue;
        }
        if (background[ny * width + nx]) {
          touchesBackground = true;
          break;
        }
      }
    }
    if (!touchesBackground) {
      continue;
    }
    const light = minChannel(i);
    if (light <= FRINGE_MIN_CHANNEL) {
      continue;
    }
    const faded = Math.round(
      (255 * (255 - light)) / (255 - FRINGE_MIN_CHANNEL)
    );
    data[i * 4 + 3] = Math.min(data[i * 4 + 3] ?? 255, faded);
  }

  for (let i = 0; i < count; i++) {
    if (background[i]) {
      data[i * 4 + 3] = 0;
    }
  }
  return data;
}

/**
 * Tint grayscale RGBA pixel data toward `rgb`: each pixel becomes
 * `color * (luma/255)` so darker source pixels stay darker in the tint (a
 * multiply). Alpha preserved. Pure/testable.
 */
export function tintGrayscale(
  data: Uint8ClampedArray,
  rgb: [number, number, number]
): Uint8ClampedArray {
  const [tr, tg, tb] = rgb;
  for (let i = 0; i < data.length; i += 4) {
    // Source is already grayscale, so any channel is the luma.
    const f = (data[i] ?? 0) / 255;
    data[i] = tr * f;
    data[i + 1] = tg * f;
    data[i + 2] = tb * f;
  }
  return data;
}

/**
 * Grayscale equivalent of a hex color, as "#RRGGBB". This is what "render in
 * grayscale" means for a TEXT watermark: the chosen color collapses to
 * its luma, the same transform `toGrayscale` applies to image pixels, so the
 * two types behave consistently. An unparseable color falls back to black.
 */
export function grayscaleHex(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) {
    return '#000000';
  }
  const y = Math.round(luminance(rgb[0], rgb[1], rgb[2]));
  const c = Math.max(0, Math.min(255, y)).toString(16).padStart(2, '0');
  return `#${c}${c}${c}`;
}

/**
 * The whole of an inline image we are willing to DECODE at upload time: a media
 * type from the uploader's allowlist, then base64 and nothing else. Narrowed to
 * three types rather than any `image/*` because this is the same allowlist as
 * WATERMARK_IMAGE_ACCEPT, and a type we never produce has no business reaching
 * an `<img>`.
 */
const DECODABLE_IMAGE_DATA_URL =
  /^data:image\/(png|jpeg|svg\+xml);base64,([A-Za-z0-9+/]*={0,2})$/i;

/**
 * Longest image data URL we will accept back from shared storage.
 *
 * Base64 inflates by ~4/3, so this is {@link MAX_WATERMARK_IMAGE_BYTES} plus the
 * header, rounded up. The upload cap alone never protected this: it is checked
 * against a `File` the local user picked, while the values that actually get
 * rendered come from an SDO and a document asset that any org member can write
 * through the public GraphQL API.
 */
export const MAX_STORED_IMAGE_DATA_URL_CHARS = Math.ceil(
  (MAX_WATERMARK_IMAGE_BYTES * 4) / 3 + 64
);

/** PNG magic, as the eight bytes it actually is. */
const PNG_SIGNATURE = [137, 80, 78, 71, 13, 10, 26, 10];

/**
 * Pixel size of a base64 PNG, read from its header — no decode, no canvas.
 *
 * A PNG opens with an 8-byte signature and then IHDR, whose first two fields
 * are width and height as big-endian uint32. That is 24 bytes in, which is 32
 * base64 characters — so the dimensions of an image of any size can be had from
 * the front of the string, synchronously, for the cost of one `atob` of 32
 * chars.
 *
 * That property is what lets the size check live at the same boundary as every
 * other check on this value. The alternative — decoding into an `<img>` to ask
 * `naturalWidth` — is async, so it would have to move to the fetch saga and the
 * open-document hydrate, splitting one rule across two places and leaving the
 * settings table (which renders straight from the store) unprotected in the gap.
 */
function pngPixelSize(
  base64: string
): { width: number; height: number } | null {
  if (base64.length < 32) {
    return null;
  }
  let head: string;
  try {
    head = atob(base64.slice(0, 32));
  } catch {
    return null;
  }
  if (head.length < 24) {
    return null;
  }
  const byte = (i: number) => head.charCodeAt(i) & 0xff;
  if (PNG_SIGNATURE.some((b, i) => byte(i) !== b)) {
    return null;
  }
  // Bytes 12..15 are the chunk type; it must be IHDR for 16..23 to be the size.
  if (String.fromCharCode(byte(12), byte(13), byte(14), byte(15)) !== 'IHDR') {
    return null;
  }
  const uint32 = (o: number) =>
    ((byte(o) << 24) |
      (byte(o + 1) << 16) |
      (byte(o + 2) << 8) |
      byte(o + 3)) >>>
    0;
  return { width: uint32(16), height: uint32(20) };
}

/**
 * Vet an image data URL arriving from shared storage — the SDO library or a
 * document's redaction asset. Returns the value, or `null` if it has no
 * business being rendered.
 *
 * Stricter than the upload-side {@link dataUrlToBlob}, deliberately, and the
 * difference is the point: uploads may be `.svg`/`.jpg`, but the app only ever
 * PERSISTS `canvas.toDataURL('image/png')`, so anything else coming back is not
 * ours. The base64 alphabet is enforced rather than assumed, which is what makes
 * the value safe to place in a CSS `url()` — no quote, paren or whitespace can
 * survive this check.
 *
 * Three bounds, because each one lets something past the others: the media type
 * (it is a PNG we wrote), the LENGTH (it is not a huge payload), and the PIXEL
 * SIZE (it is not a small payload that decodes huge). The last is the one that
 * was missing — a uniform 20000x20000 PNG is well inside the byte cap and asks
 * every colleague's browser for a gigabyte-scale decode on page load.
 *
 * Not a substitute for server-side enforcement (SEC-R5); it is the client
 * refusing to render what it did not write.
 */
export function sanitizeStoredImageDataUrl(
  src: string | null | undefined
): string | null {
  if (typeof src !== 'string' || src.length > MAX_STORED_IMAGE_DATA_URL_CHARS) {
    return null;
  }
  const match = /^data:image\/png;base64,([A-Za-z0-9+/]+={0,2})$/.exec(src);
  if (!match) {
    return null;
  }
  const size = pngPixelSize(match[1] as string);
  if (
    !size ||
    size.width < 1 ||
    size.height < 1 ||
    size.width > MAX_STORED_IMAGE_EDGE ||
    size.height > MAX_STORED_IMAGE_EDGE
  ) {
    return null;
  }
  return src;
}

/**
 * Read a File as a data URL.
 *
 * Reinstated deliberately. The DECODE path stopped needing this when uploads
 * moved to Blob + object URL — a large image no longer becomes a multi-million character
 * string on its way to `<img>`. But STORING the source does need a string,
 * because an SDO holds JSON, so this is back for that one purpose and is not a
 * step on the render path.
 */
function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      typeof reader.result === 'string'
        ? resolve(reader.result)
        : reject(new Error('Could not read file'));
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Media types that may be KEPT as a downloadable original. Deliberately not the
 * uploader's allowlist: `.svg` is missing, and its absence is the control.
 *
 * SEC-R2 requires an uploaded SVG to be sanitized, because SVG carries script.
 * We do not sanitize it — a correct SVG sanitizer is a dependency and a
 * standing obligation, not a regex — so we do not keep one. A `.svg` upload is
 * rasterized like any other and its source is discarded; Download then hands
 * back the PNG we made, which is inert by construction.
 *
 * PNG and JPEG are kept as-is because they are not an active-content format:
 * there is no script to strip, so storing the bytes unmodified adds nothing a
 * sanitizer would remove.
 */
const STORABLE_SOURCE_DATA_URL =
  /^data:image\/(png|jpeg);base64,([A-Za-z0-9+/]*={0,2})$/i;

/** Whether an upload's own bytes may be kept — see {@link STORABLE_SOURCE_DATA_URL}. */
const SOURCE_STORABLE_EXTENSIONS = /\.(png|jpe?g)$/i;

/**
 * Vet a SOURCE image coming back from shared storage.
 *
 * Same strictness as {@link sanitizeStoredImageDataUrl} on media type and
 * alphabet, and for the same reason: this value is org-writable through the
 * public API, so what comes back is not necessarily what this app wrote. A row
 * carrying an SVG source — written before that stopped being allowed, or by
 * something that is not this app — is refused here and simply has no download.
 *
 * No pixel bound, unlike the render path: this is never decoded. It goes from
 * the store to a file the user asked to save.
 */
export function sanitizeStoredSourceDataUrl(
  src: string | null | undefined
): string | null {
  if (typeof src !== 'string' || src.length > MAX_STORED_IMAGE_DATA_URL_CHARS) {
    return null;
  }
  return STORABLE_SOURCE_DATA_URL.test(src) ? src : null;
}

/**
 * Turn a vetted inline image into binary, or `null` if it is not one.
 *
 * The single gate for anything this module decodes, and deliberately the only
 * one: an earlier revision kept a string-returning validator alongside it, so
 * two functions each described "the allowlist" while one of them was enforced —
 * the shape of drift this module has already been bitten by once.
 *
 * The check is a decode, not string surgery. The payload becomes bytes and the
 * bytes become a Blob tagged with a media type drawn from the fixed list, so
 * nothing the caller wrote survives as text into what gets loaded. A value that
 * matches the alphabet but is not decodable base64 is rejected here rather than
 * reaching `onerror`.
 *
 * Callers: `loadImage`, which is how a data URL the app itself produced (own
 * canvas output, or a value already cleared by `sanitizeStoredImageDataUrl`)
 * gets decoded for a recolor or a grayscale pass. The upload path does NOT come
 * through here — a picked `File` is already a Blob and goes straight to
 * `loadImageBlob` — so the jpeg and svg+xml branches of the pattern are reachable
 * only in principle. They stay because the allowlist should describe what this
 * app accepts, not what today's call graph happens to reach.
 *
 * Values coming back from the org-shared SDO and the document asset are the
 * untrusted boundary and have their own, stricter check:
 * {@link sanitizeStoredImageDataUrl}.
 *
 * Exported for tests: this is where the allowlist actually lives, so it is
 * where the allowlist should be asserted.
 */
export function dataUrlToBlob(src: string): Blob | null {
  const match =
    typeof src === 'string' ? DECODABLE_IMAGE_DATA_URL.exec(src) : null;
  const mediaType = match?.[1];
  const payload = match?.[2];
  if (mediaType === undefined || payload === undefined) {
    return null;
  }
  let binary: string;
  try {
    binary = atob(payload);
  } catch {
    // Right alphabet, wrong length — base64 that cannot decode.
    return null;
  }
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: `image/${mediaType.toLowerCase()}` });
}

/**
 * Decode a Blob into an `<img>`, through an object URL.
 *
 * The object URL is the point, and not only for the scanner. `<img>` is the
 * only decoder that handles SVG — `createImageBitmap` rejects an SVG carrying
 * just a `viewBox`, which is most of them — so the element cannot be avoided,
 * but the megabytes can: an object URL is an opaque `blob:` handle the browser
 * mints, so a large upload is never inflated into a multi-million character base64
 * string just to be handed to `src`. The upload path now goes File -> Blob ->
 * handle, never touching text at all.
 *
 * Revoked as soon as the image resolves; by then it is decoded and safe to
 * draw. Skipping that leaks the whole blob for the life of the document.
 */
function loadImageBlob(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob);
    const img = new Image();
    const settle = (finish: () => void) => {
      URL.revokeObjectURL(objectUrl);
      finish();
    };
    img.onload = () => settle(() => resolve(img));
    img.onerror = () =>
      settle(() => reject(new Error('Could not decode image')));
    img.src = objectUrl;
  });
}

/** Load an inline image data URL into an HTMLImageElement (decoded). */
function loadImage(src: string): Promise<HTMLImageElement> {
  const blob = dataUrlToBlob(src);
  return blob === null
    ? Promise.reject(new Error('Unsupported image source'))
    : loadImageBlob(blob);
}

/** Draw an image onto a fresh 2D canvas context, bounded to RASTER_MAX_EDGE. */
function drawToCanvas(img: HTMLImageElement): {
  ctx: CanvasRenderingContext2D;
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
} {
  // SVGs may report 0 natural size; fall back to a square default.
  const naturalW = img.naturalWidth || img.width || RASTER_MAX_EDGE;
  const naturalH = img.naturalHeight || img.height || RASTER_MAX_EDGE;
  const longest = Math.max(naturalW, naturalH);
  const factor = longest > RASTER_MAX_EDGE ? RASTER_MAX_EDGE / longest : 1;
  const width = Math.max(1, Math.round(naturalW * factor));
  const height = Math.max(1, Math.round(naturalH * factor));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas 2D context unavailable');
  }
  ctx.drawImage(img, 0, 0, width, height);
  return { ctx, canvas, width, height };
}

export interface ProcessedWatermarkImage {
  /**
   * Rasterized PNG data URL with the image's ORIGINAL COLORS — the base every
   * drawn form is derived from. Never pre-converted to grayscale: that choice belongs
   * to the applied watermark and has to stay reversible.
   */
  baseDataUrl: string;
  /**
   * The picked file, UNTOUCHED, as a data URL — an SVG stays SVG, a JPEG stays
   * JPEG.
   *
   * The upload's own bytes, kept so the artwork can be downloaded back out —
   * `baseDataUrl` is a lossy 1024px raster of it, and rasterizing is one-way.
   *
   * Absent for `.svg`, and only for `.svg`: keeping one would mean storing and
   * redistributing script-capable content this app does not sanitize (SEC-R2).
   * An SVG upload downloads as the PNG instead. Nothing renders this field
   * either way.
   */
  sourceDataUrl?: string;
  /**
   * A small PNG of the same artwork — the ONLY image form that stays inline on
   * the SDO row (§K). Bounded on its longest edge, aspect preserved.
   *
   * Everything that merely LISTS watermarks reads this: the settings table, the
   * editor picker, the batch picker, the drawer's own preview. The full raster
   * is fetched from the watermark's TDO only when a mark is actually applied,
   * which is what keeps a library fetch small enough to run on three route
   * entries without anyone noticing.
   */
  previewDataUrl: string;
  /** The original filename, extension included — so `logo.svg` stays truthful. */
  name: string;
  /**
   * Rasterized height / width. Measured here, once, because this is the only
   * place that already holds a decoded image — which spares every consumer from
   * decoding it again just to lay it out. A grayscale or tinted derivation keeps the
   * canvas dimensions, so this stays valid for the life of the watermark.
   */
  aspect: number;
}

/**
 * Rasterize an uploaded watermark image file, preserving its colors. Throws on
 * an oversized file or an undecodable image so the caller can surface a message.
 *
 * Rasterizing through `<img>` + `<canvas>` is also what satisfies SEC-R2: an
 * uploaded `.svg` is decoded in the browser's non-scripted image context, never
 * executed, and only the resulting PNG is stored — the SVG source never reaches
 * the SDO or any other viewer.
 *
 * That guarantee is why `sourceDataUrl` is set for PNG and JPEG uploads and NOT
 * for SVG. The source exists so Download can return the original artwork, and
 * for a raster upload the bytes are inert and go back verbatim. For an SVG they
 * would be script-capable content stored in an org-shared record and handed to
 * colleagues under this app's name, so the source is dropped and Download
 * returns the rasterized PNG. Restoring it means sanitizing first — see
 * {@link STORABLE_SOURCE_DATA_URL}.
 */
/** Extensions the uploader accepts, derived from {@link WATERMARK_IMAGE_ACCEPT}. */
const ACCEPTED_EXTENSIONS = /\.(svg|png|jpe?g)$/i;

export async function processWatermarkImageFile(
  file: File
): Promise<ProcessedWatermarkImage> {
  // Checked here, not only on the file input: `accept=` is a picker hint the
  // drag-and-drop path never sees, so a drop was the one way in with no type
  // check at all. `file.type` follows the extension and is therefore chosen by
  // whoever named the file — this is a usability guard, not a security one. The
  // security property comes from re-encoding: whatever decodes, we store our own
  // PNG of it, and whatever does not decode throws below.
  if (!ACCEPTED_EXTENSIONS.test(file.name)) {
    throw new Error('Accepted formats: .svg, .png, .jpg.');
  }
  if (file.size > MAX_WATERMARK_IMAGE_BYTES) {
    throw new Error(`Image is too large (max ${MAX_WATERMARK_IMAGE_LABEL}).`);
  }
  // A File is already a Blob, so the picked bytes go straight to the decoder.
  // Reading it into a data URL first, as this used to, built a ~2.7M character
  // string for a large image only to hand it back to the browser.
  const img = await loadImageBlob(file);
  // Read separately, and only to be STORED. The decode above never sees it.
  // Skipped entirely for SVG rather than read-then-discarded, so there is no
  // point at which the script-capable bytes exist as a value something could
  // later be tempted to persist.
  const sourceDataUrl = SOURCE_STORABLE_EXTENSIONS.test(file.name)
    ? await readFileAsDataUrl(file)
    : undefined;
  const { canvas, width, height } = drawToCanvas(img);
  const baseDataUrl = canvas.toDataURL('image/png');
  // The 5 MB cap above is on the FILE. What gets stored is our own re-encoded
  // PNG, and re-encoding is not guaranteed to shrink: a small .svg that
  // rasterizes to a noisy 1024x1024 (a turbulence fill, a dense map) deflates
  // worse than the vector source it came from. Without this check the app
  // happily saved a record that `sanitizeStoredImageDataUrl` then refused on
  // the next page load, leaving a named image row with nothing to draw and no
  // explanation — silent data loss on the app's own output. The read boundary
  // has to be something the write boundary cannot exceed.
  if (baseDataUrl.length > MAX_STORED_IMAGE_DATA_URL_CHARS) {
    throw new Error(
      `Image is too detailed to store (max ${MAX_WATERMARK_IMAGE_LABEL} once converted). ` +
        'Try a simpler image or a smaller one.'
    );
  }
  // Checked, not assumed to fit. Both constants derive from
  // MAX_WATERMARK_IMAGE_BYTES — the cap is that number inflated by base64's 4/3
  // plus a header — so a maximum-size file fits by a handful of characters, and
  // that margin is arithmetic rather than a guarantee. Keep the check: an
  // unchecked source is one that `sanitizeStoredSourceDataUrl` refuses on the
  // next page load, which is the silent-data-loss shape the base check above
  // was added to close.
  if (
    sourceDataUrl !== undefined &&
    sourceDataUrl.length > MAX_STORED_IMAGE_DATA_URL_CHARS
  ) {
    throw new Error(`Image is too large (max ${MAX_WATERMARK_IMAGE_LABEL}).`);
  }
  return {
    baseDataUrl,
    sourceDataUrl,
    previewDataUrl: makePreviewDataUrl(img),
    name: file.name,
    aspect: width > 0 ? height / width : 1,
  };
}

/**
 * Longest edge, in pixels, of the inline preview stored on the SDO row (§K).
 *
 * The library row keeps a small preview and nothing else — the working raster
 * and the original upload both live on the watermark's own TDO — so this number
 * is the whole cost of listing the library. Measured on real artwork at 256px:
 * ~38 KB of base64 for a square seal, ~8 KB for a 3.4:1 wordmark. 256 rather
 * than 128 so the edit drawer can render from the preview without fetching the
 * raster, and so the table stays crisp on a retina display.
 */
const PREVIEW_MAX_EDGE = 256;

/**
 * A small PNG of `img`, bounded to {@link PREVIEW_MAX_EDGE} on its LONGEST edge.
 *
 * Aspect is preserved rather than fitted into a square. A wordmark is commonly
 * 3:1 or wider, and squaring it either distorts the artwork or pads it — and the
 * padding is what would be paid for, since a square seal encodes to roughly four
 * times a wordmark's size at the same edge.
 *
 * Never upscales: a 64px favicon-sized upload stays 64px rather than being blown
 * up to 256 and stored at several times the bytes for no added detail.
 */
function makePreviewDataUrl(img: HTMLImageElement): string {
  const naturalW = img.naturalWidth || img.width || PREVIEW_MAX_EDGE;
  const naturalH = img.naturalHeight || img.height || PREVIEW_MAX_EDGE;
  const longest = Math.max(naturalW, naturalH);
  const factor = longest > PREVIEW_MAX_EDGE ? PREVIEW_MAX_EDGE / longest : 1;
  const width = Math.max(1, Math.round(naturalW * factor));
  const height = Math.max(1, Math.round(naturalH * factor));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas 2D context unavailable');
  }
  ctx.drawImage(img, 0, 0, width, height);
  return canvas.toDataURL('image/png');
}

/**
 * Grayscale a watermark image — the image half of "render either in black and
 * white". Derived from the color base on demand, so turning it back off
 * restores the original colors without a re-upload.
 */
export async function toGrayscaleImage(baseDataUrl: string): Promise<string> {
  const img = await loadImage(baseDataUrl);
  const { ctx, canvas, width, height } = drawToCanvas(img);
  const imageData = ctx.getImageData(0, 0, width, height);
  toGrayscale(imageData.data);
  ctx.putImageData(imageData, 0, 0);
  return canvas.toDataURL('image/png');
}

/**
 * Resolve the PNG a watermark should actually be DRAWN with, from its color
 * base plus the two user choices. One place, so the editor panel and the
 * settings drawer can never derive it differently.
 *
 * Grayscale wins over a tint: "render in grayscale" is a statement about the
 * final appearance, so a color left over from before the toggle was flipped
 * must not leak back into the output. The color itself is kept on the model,
 * so turning grayscale off restores the tint.
 */
export async function deriveWatermarkImage(
  baseDataUrl: string,
  opts: { grayscale: boolean; color: string }
): Promise<string> {
  // BEFORE the tone, not after, and the ordering is the whole correctness of
  // it. `tintGrayscale` maps every pixel to `tint * (luma/255)`, so white paper
  // — luma 255 — comes out as EXACTLY the tint colour. Its smallest channel is
  // then nowhere near `PAPER_MIN_CHANNEL`, the key finds no paper at all, and a
  // tinted image mark keeps the solid block this pass exists to remove. §H2 of
  // `plans/VE-24693-watermark-data-model-revisions.md` says the same thing, and
  // it was written before this was built; running the key second was a mistake.
  //
  // Safe in the other direction: `toGrayscale` and `tintGrayscale` both
  // preserve alpha, so pixels this removes stay removed through the tone.
  //
  // Done here rather than at either call site because this is the one function
  // both surfaces derive through — the overlay renders its result and
  // `toBurnInWatermark` sends it — which is what stops the preview and the
  // export disagreeing about what a mark covers.
  //
  // Best-effort. A raster that will not decode, or a canvas this browser will
  // not give us, leaves the mark exactly as it was rather than dropping it:
  // washing out some text is a defect, and a watermark that vanished because a
  // cosmetic pass failed is a worse one.
  // Only when the key is the active mode — see WATERMARK_WHITE_REMOVAL. Under
  // 'multiply' the raster is left alone and the blend does the work on both
  // surfaces instead.
  let keyed = baseDataUrl;
  if (WATERMARK_WHITE_REMOVAL === 'knockout') {
    try {
      keyed = await knockOutWhiteBackground(baseDataUrl);
    } catch (err) {
      console.warn('Watermark background could not be removed', err);
    }
  }
  return opts.grayscale
    ? await toGrayscaleImage(keyed)
    : opts.color
      ? await recolorWatermarkImage(keyed, opts.color)
      : keyed;
}

/**
 * {@link knockOutBackground} over a data URL: decode, key, re-encode.
 *
 * PNG out, always — the format the rest of this feature requires, and the only
 * one of the two that can carry the alpha this just created. Re-encoding a
 * keyed image as JPEG would composite it back onto black.
 */
export async function knockOutWhiteBackground(
  baseDataUrl: string
): Promise<string> {
  const img = await loadImage(baseDataUrl);
  const { ctx, canvas, width, height } = drawToCanvas(img);
  const imageData = ctx.getImageData(0, 0, width, height);
  // Measured on the buffer BEFORE the in-place key, which is what makes the
  // question answerable: how much of this image was never the fill's to take.
  const artwork = artworkPixelCount(imageData.data);
  knockOutBackground(imageData.data, width, height);
  const after = opaquePixelCount(imageData.data);
  if (keyWouldEraseMark(artwork, after)) {
    console.warn(
      `[redact] Watermark background removal would have erased the mark ` +
        `(${artwork} artwork pixels, ${after} visible after); ` +
        `drawing it unchanged.`
    );
    return baseDataUrl;
  }
  ctx.putImageData(imageData, 0, 0);
  return canvas.toDataURL('image/png');
}

/**
 * How many pixels of `data` would actually paint something.
 *
 * Counted either side of the key so the guard measures what the operator would
 * SEE disappear, rather than how many pixels the fill happened to touch — a
 * mark that was already mostly transparent has not been harmed by removing the
 * rest of its transparency.
 */
export function opaquePixelCount(data: Uint8ClampedArray): number {
  let count = 0;
  for (let i = 3; i < data.length; i += 4) {
    if ((data[i] ?? 0) >= 8) {
      count += 1;
    }
  }
  return count;
}

/**
 * How many pixels would paint something the key is NOT entitled to remove.
 *
 * The same two tests `isPaper` applies, inverted: visible, and not near-white
 * on every channel. This is the population the fill leaves alone by
 * construction, which is why it is the right denominator for
 * {@link keyWouldEraseMark} and the count of opaque pixels is not.
 */
export function artworkPixelCount(data: Uint8ClampedArray): number {
  let count = 0;
  for (let i = 0; i + 3 < data.length; i += 4) {
    if ((data[i + 3] ?? 0) < 8) {
      continue;
    }
    const min = Math.min(data[i] ?? 0, data[i + 1] ?? 0, data[i + 2] ?? 0);
    if (min < PAPER_MIN_CHANNEL) {
      count += 1;
    }
  }
  return count;
}

/**
 * Tint a watermark image to `hexColor` — the image half of "both image and text
 * watermarks support color".
 *
 * `tintGrayscale` multiplies a color through a luma, so the base is flattened to
 * grayscale first. That step is not optional now that the base carries the
 * image's original colors: tinting a color image directly would multiply the
 * tint through the wrong channel and shift hues. Returns the base unchanged for
 * an unparseable color.
 */
export async function recolorWatermarkImage(
  baseDataUrl: string,
  hexColor: string
): Promise<string> {
  const rgb = hexToRgb(hexColor);
  if (!rgb) {
    return baseDataUrl;
  }
  const img = await loadImage(baseDataUrl);
  const { ctx, canvas, width, height } = drawToCanvas(img);
  const imageData = ctx.getImageData(0, 0, width, height);
  toGrayscale(imageData.data);
  tintGrayscale(imageData.data, rgb);
  ctx.putImageData(imageData, 0, 0);
  return canvas.toDataURL('image/png');
}
